import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FaStore } from 'react-icons/fa'
import Spinner from './spinner'
import api from './api-client'
import notify from './notifications-helper'
import { decodeCompanyIdParam } from './impact-network-id'

type RouteProps = {
  match: {
    params: {
      id: string
    }
  }
}

type AlignedValue = {
  slug?: string
  label?: string
  color?: string
  icon?: { family?: string; name?: string } | null
}

type PartnerIndicator = {
  name?: string
  impactAreas?: string[]
}

type PartnerCompany = {
  _id?: string
  name?: string
  description?: string
  segment?: string
  website?: string
  logourl?: string | null
  street?: string
  city?: string
  state?: string
  zipCode?: string
  phone?: string
  email?: string
  sbaProfileUrl?: string
  tags?: string[]
  certifications?: string[]
  mission_flags?: string[]
  matchedValues?: string[]
  indicators?: PartnerIndicator[]
  matchScore?: number
  valuesAligned?: AlignedValue[]
}

const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

const Chip: React.FC<{ label: string; color?: string }> = ({ label, color }) => {
  return (
    <span
      style={{
        display: 'inline-block',
        margin: '0 0.4em 0.4em 0',
        padding: '0.2em 0.6em',
        borderRadius: '999px',
        background: color || '#e9ecef',
        color: color ? '#fff' : '#333',
        fontSize: '0.9em',
      }}
    >
      {label}
    </span>
  )
}

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  return (
    <div className="form-group">
      <label>{label}</label>
      <div>{children}</div>
    </div>
  )
}

const PartnerLogo: React.FC<{ url?: string | null; name?: string }> = ({ url, name }) => {
  const validUrl = isHttpUrl(url) ? url.trim() : ''
  const [failed, setFailed] = useState(!validUrl)

  useEffect(() => {
    setFailed(!validUrl)
  }, [validUrl])

  const showFallback = failed || !validUrl

  return (
    <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div
        style={{
          width: 160,
          height: 160,
          border: '1px solid #ddd',
          borderRadius: 8,
          background: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {showFallback ? (
          <FaStore size="3em" color="#888" title="Logo unavailable" />
        ) : (
          <img
            src={validUrl}
            alt={`${name || 'Company'} logo`}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={() => setFailed(true)}
          />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <label>Logo URL</label>
        {validUrl ? (
          <div>
            <a href={validUrl} target="_blank" rel="noopener noreferrer">
              {validUrl}
            </a>
          </div>
        ) : (
          <div className="text-muted">No logo URL</div>
        )}
        {showFallback && validUrl ? (
          <div className="text-muted" style={{ marginTop: '0.5em' }}>
            Image could not be loaded; showing fallback.
          </div>
        ) : null}
      </div>
    </div>
  )
}

const stringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && !!item.trim())
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

const optionalString = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value : undefined
}

const nonBlankString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  if (!value.trim()) return null
  return value
}

const companyName = (value: unknown): string | null => {
  if (typeof value === 'string') return nonBlankString(value)
  if (!isRecord(value)) return null
  const legal = nonBlankString(value.legal)
  if (legal) return legal
  const name = nonBlankString(value.name)
  if (name) return name
  return null
}

const alignedValues = (value: unknown): AlignedValue[] => {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!isRecord(item)) return []
    const slug = nonBlankString(item.slug)
    if (!slug) return []
    let icon: AlignedValue['icon'] = null
    if (isRecord(item.icon)) {
      icon = {
        family: optionalString(item.icon.family),
        name: optionalString(item.icon.name),
      }
    }
    return [{
      slug,
      label: optionalString(item.label),
      color: optionalString(item.color),
      icon,
    }]
  })
}

const partnerIndicators = (value: unknown): PartnerIndicator[] => {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!isRecord(item)) return []
    const name = nonBlankString(item.name)
    if (!name) return []
    if (item.impactAreas !== undefined && !Array.isArray(item.impactAreas)) return []
    if (
      Array.isArray(item.impactAreas) &&
      item.impactAreas.some((area) => typeof area !== 'string')
    ) {
      return []
    }
    return [{
      name,
      impactAreas: stringList(item.impactAreas),
    }]
  })
}

const toPartnerCompany = (value: unknown): PartnerCompany | null => {
  if (!isRecord(value)) return null
  const name = companyName(value.name)
  if (name === null) return null
  return {
    _id: optionalString(value._id),
    name,
    description: optionalString(value.description),
    segment: optionalString(value.segment),
    website: optionalString(value.website),
    logourl: typeof value.logourl === 'string' || value.logourl === null ? value.logourl : undefined,
    street: optionalString(value.street),
    city: optionalString(value.city),
    state: optionalString(value.state),
    zipCode: optionalString(value.zipCode),
    phone: optionalString(value.phone),
    email: optionalString(value.email),
    sbaProfileUrl: optionalString(value.sbaProfileUrl),
    tags: stringList(value.tags),
    certifications: stringList(value.certifications),
    mission_flags: stringList(value.mission_flags),
    matchedValues: stringList(value.matchedValues),
    indicators: partnerIndicators(value.indicators),
    matchScore: typeof value.matchScore === 'number' ? value.matchScore : undefined,
    valuesAligned: alignedValues(value.valuesAligned),
  }
}

const ImpactNetworkCompanyView: React.FC<RouteProps> = ({ match }) => {
  const dispatch = useDispatch()
  const companyId = decodeCompanyIdParam(match.params.id)
  const [loading, setLoading] = useState(true)
  const [company, setCompany] = useState<PartnerCompany | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.getCompany(companyId)
      .then((co) => {
        if (!cancelled) setCompany(toPartnerCompany(co))
      })
      .catch((ex) => {
        dispatch(notify.error({ message: ex && ex.message ? ex.message : 'Failed to load company' }))
        if (!cancelled) setCompany(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [companyId, dispatch])

  const values = useMemo(() => {
    return Array.isArray(company?.valuesAligned) ? company.valuesAligned : []
  }, [company])

  if (loading) {
    return <Spinner variant="block" />
  }

  if (!company) {
    return (
      <div className="container" style={{ marginTop: '2em' }}>
        <div className="alert alert-warning">Company not found</div>
      </div>
    )
  }

  const tags = stringList(company.tags)
  const certifications = stringList(company.certifications)
  const missionFlags = stringList(company.mission_flags)
  const matchedValues = stringList(company.matchedValues)
  const indicators = Array.isArray(company.indicators) ? company.indicators : []

  return (
    <div className="container" style={{ marginTop: '2em', maxWidth: 960 }}>
      <div className="alert alert-info">Read-only Partner record</div>
      <h2>{company.name || 'Untitled company'}</h2>
      <div className="card" style={{ marginTop: '1em' }}>
        <div className="card-body">
          <FieldRow label="Logo">
            <PartnerLogo url={company.logourl} name={company.name} />
          </FieldRow>
          <FieldRow label="ID">
            <code>{company._id || companyId}</code>
          </FieldRow>
          {company.description ? (
            <FieldRow label="Description">{company.description}</FieldRow>
          ) : null}
          {company.segment ? (
            <FieldRow label="Segment">{company.segment}</FieldRow>
          ) : null}
          {company.website ? (
            <FieldRow label="Website">
              {isHttpUrl(company.website) ? (
                <a href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              ) : (
                company.website
              )}
            </FieldRow>
          ) : null}
          {(company.street || company.city || company.state || company.zipCode) ? (
            <FieldRow label="Location">
              {[company.street, company.city, company.state, company.zipCode].filter(Boolean).join(', ')}
            </FieldRow>
          ) : null}
          {company.phone ? <FieldRow label="Phone">{company.phone}</FieldRow> : null}
          {company.email ? <FieldRow label="Email">{company.email}</FieldRow> : null}
          {company.sbaProfileUrl && isHttpUrl(company.sbaProfileUrl) ? (
            <FieldRow label="SBA profile">
              <a href={company.sbaProfileUrl} target="_blank" rel="noopener noreferrer">
                {company.sbaProfileUrl}
              </a>
            </FieldRow>
          ) : company.sbaProfileUrl ? (
            <FieldRow label="SBA profile">{company.sbaProfileUrl}</FieldRow>
          ) : null}
          {typeof company.matchScore === 'number' ? (
            <FieldRow label="Match score">{company.matchScore}</FieldRow>
          ) : null}
          <FieldRow label="Values">
            {values.length ? values.map((v, idx) => (
              <Chip
                key={`${v.slug || v.label || idx}`}
                label={v.label || v.slug || 'Value'}
                color={typeof v.color === 'string' ? v.color : undefined}
              />
            )) : <span className="text-muted">None</span>}
          </FieldRow>
          <FieldRow label="Tags">
            {tags.length ? tags.map((tag) => <Chip key={tag} label={tag} />) : <span className="text-muted">None</span>}
          </FieldRow>
          {certifications.length ? (
            <FieldRow label="Certifications">
              {certifications.map((item) => <Chip key={item} label={item} />)}
            </FieldRow>
          ) : null}
          {missionFlags.length ? (
            <FieldRow label="Mission flags">
              {missionFlags.map((item) => <Chip key={item} label={item} />)}
            </FieldRow>
          ) : null}
          {matchedValues.length ? (
            <FieldRow label="Matched values">
              {matchedValues.map((item) => <Chip key={item} label={item} />)}
            </FieldRow>
          ) : null}
          {indicators.length ? (
            <FieldRow label="Indicators">
              <ul style={{ marginBottom: 0 }}>
                {indicators.map((ind, idx) => (
                  <li key={`${ind.name || idx}`}>
                    {ind.name || 'Indicator'}
                    {Array.isArray(ind.impactAreas) && ind.impactAreas.length
                      ? ` (${ind.impactAreas.join(', ')})`
                      : ''}
                  </li>
                ))}
              </ul>
            </FieldRow>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ImpactNetworkCompanyView
